import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { refreshAll } from '../../services/refreshService';

// === TYPES ===
interface GenericSidebarProps {
  title: string;
  titlePath: string;
  isWide: boolean;
  onToggleWidth: () => void;
  loading: boolean;
  error: string | null;
  filterControls: React.ReactNode;
  tableContent: React.ReactNode;
  actionButtons: React.ReactNode;
  dialogs?: React.ReactNode; // For context menus and other dialogs
}

// === CONSTANTS ===
const SIDEBAR_WIDTHS = {
  compact: 426,
  wide: 800
} as const;


// === MAIN COMPONENT ===
const GenericSidebar: React.FC<GenericSidebarProps> = ({
  title,
  titlePath,
  isWide,
  onToggleWidth,
  loading,
  error,
  filterControls,
  tableContent,
  actionButtons,
  dialogs
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentWidth = isWide ? SIDEBAR_WIDTHS.wide : SIDEBAR_WIDTHS.compact;
  const contentWidth = currentWidth - 46; // Abzug für Padding

  // === LOADING & ERROR STATES ===
  if (loading) {
    return (
      <Paper sx={{ width: currentWidth, flexShrink: 0, height: 'calc(100vh - 70px)', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.default', boxShadow: 'none' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ width: currentWidth, flexShrink: 0, height: 'calc(100vh - 70px)', p: 3, bgcolor: 'background.default', boxShadow: 'none' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  // === MAIN RENDER ===
  return (
    <Paper sx={{
      width: currentWidth,
      flexShrink: 0,
      height: 'calc(100vh - 70px)', // Zurück zur ursprünglichen Höhe
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      boxShadow: 'none',
      bgcolor: 'background.default',
      transition: 'width 0.1s ease-in-out',
    }}>
      <Box sx={{
        width: contentWidth,
        overflowY: 'auto',
        paddingTop: '6.25rem',
        flex: 1,
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '3.25rem',
          bottom: '3.25rem',
          height: 'auto',
          width: '2px',
          backgroundColor: 'divider',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Header mit Toggle-Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            variant="h4"
            onClick={() => { navigate(titlePath); refreshAll() }}
            sx={{
              color: 'primary.dark',
              cursor: 'pointer',
              '&:hover': { color: 'primary.blue' }
            }}
            data-testid="generic-sidebar-title"
          >
            {title}
          </Typography>
          <Tooltip title={isWide ? t('common:sidebar.collapse') : t('common:sidebar.expand')}>
            <IconButton onClick={onToggleWidth} size="small" data-testid="generic-sidebar-toggle-button">
              {isWide ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filter Controls */}
        {filterControls}

        {/* Table Content */}
        {tableContent}
      </Box>

      {/* Action Buttons */}
      {actionButtons}

      {/* Dialogs */}
      {dialogs}
    </Paper>
  );
};

export default GenericSidebar; 